import { useEffect, useMemo, useState } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, MapPin, Calendar, X, ChevronLeft,
} from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import SEO from "@/components/SEO";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeHtml } from "@/lib/sanitize";

type GalleryCategory = {
  id: string;
  name: string;
  slug: string;
};

type ProjectCategoryRelation = {
  name?: string | null;
  slug?: string | null;
} | Array<{
  name?: string | null;
  slug?: string | null;
}> | null;

type GalleryProject = {
  id: string;
  title: string;
  subtitle: string | null;
  content_html: string | null;
  image_url: string | null;
  category_id: string | null;
  metadata: unknown;
  categories?: ProjectCategoryRelation;
};

type GalleryProjectMeta = {
  location?: string;
  year?: string;
  highlights: string[];
};

const getProjectMeta = (value: unknown): GalleryProjectMeta => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { highlights: [] };
  }

  const meta = value as Record<string, unknown>;

  return {
    location: typeof meta.location === "string" ? meta.location : undefined,
    year: typeof meta.year === "string" ? meta.year : undefined,
    highlights: Array.isArray(meta.highlights)
      ? meta.highlights.filter((item): item is string => typeof item === "string")
      : [],
  };
};

const getCategoryData = (value: ProjectCategoryRelation) => {
  if (Array.isArray(value)) {
    return value[0] ?? null;
  }

  return value ?? null;
};

const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedProject, setSelectedProject] = useState<GalleryProject | null>(null);

  const { data: categories = [] } = useQuery<GalleryCategory[]>({
    queryKey: ["categories"],
    queryFn: async () => {
      const { data, error } = await supabase.from("categories").select("*").order("sort_order");

      if (error) {
        throw error;
      }

      return data || [];
    },
  });

  const { data: projects = [], error: projectsError } = useQuery<GalleryProject[]>({
    queryKey: ["posts", "gallery"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("posts")
        .select("*, categories(name, slug)")
        .eq("section", "gallery")
        .order("sort_order");

      if (error) {
        throw error;
      }

      return (data || []) as GalleryProject[];
    },
  });

  const filteredProjects = useMemo(() => {
    if (activeCategory === "all") {
      return projects;
    }

    return projects.filter((project) => project.category_id === activeCategory);
  }, [activeCategory, projects]);

  const gridRef = useScrollReveal([filteredProjects]);

  const allCategories = useMemo(() => {
    const usedCategoryIds = new Set(
      projects
        .map((project) => project.category_id)
        .filter((categoryId): categoryId is string => typeof categoryId === "string" && categoryId.length > 0)
    );

    const relevantCategories = categories.filter((category) => usedCategoryIds.has(category.id));

    return [{ id: "all", name: "הכל", slug: "all" }, ...relevantCategories];
  }, [categories, projects]);

  useEffect(() => {
    if (activeCategory !== "all" && !allCategories.some((category) => category.id === activeCategory)) {
      setActiveCategory("all");
    }
  }, [activeCategory, allCategories]);

  useEffect(() => {
    if (selectedProject && !filteredProjects.some((project) => project.id === selectedProject.id)) {
      setSelectedProject(null);
    }
  }, [filteredProjects, selectedProject]);

  return (
    <div className="min-h-screen">
      <SEO
        title="גלריית פרויקטים – גגות רעפים, איטום ופחחות בחיפה והקריות"
        description="פרויקטים נבחרים של אור בגג: תיקון גג רעפים, איטום גגות, פחחות ומרזבים בחיפה, קריות, נשר ועכו. ראו תוצאות אמיתיות מהשטח."
        path="/gallery"
      />
      <Header />
      <main id="main">
      <section className="bg-dark pt-28 pb-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-dark-foreground mb-4">
            הפרויקטים <span className="text-primary">שלנו</span>
          </h1>
          <p className="text-lg text-dark-foreground/90 max-w-2xl mx-auto leading-relaxed">
            מבחר פרויקטים נבחרים מ-13 שנות עבודה – כל אחד מספר סיפור של מקצועיות, דיוק ותוצאה מושלמת
          </p>
          <div className="mt-6">
            <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-gold-light font-bold">
              <Link to="/">
                <ArrowLeft size={16} />
                חזרה לדף הבית
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-6 bg-background border-b border-border sticky top-16 z-40">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2">
            {allCategories.map((cat) => (
              <Button
                key={cat.id}
                type="button"
                variant={activeCategory === cat.id ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveCategory(cat.id)}
                className={
                  activeCategory === cat.id
                    ? "bg-primary text-primary-foreground hover:bg-gold-light"
                    : "border-border text-foreground hover:bg-accent"
                }
              >
                {cat.name}
              </Button>
            ))}
          </div>
        </div>
      </section>

      <section ref={gridRef} className="py-16 bg-background">
        <div className="container mx-auto px-4 max-w-6xl">
          {projectsError ? (
            <p className="text-center text-muted-foreground py-12">יש תקלה זמנית בטעינת הפרויקטים, נסו שוב בעוד רגע</p>
          ) : filteredProjects.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">אין פרויקטים בקטגוריה זו עדיין</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {filteredProjects.map((project, i) => {
                const meta = getProjectMeta(project.metadata);
                const categoryData = getCategoryData(project.categories ?? null);

                return (
                  <Card
                    key={project.id}
                    className="animate-on-scroll group cursor-pointer overflow-hidden border-border bg-card hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                    style={{ transitionDelay: `${i * 80}ms` }}
                    onClick={() => setSelectedProject(project)}
                  >
                    <div className="aspect-[16/9] bg-dark relative overflow-hidden">
                      {project.image_url ? (
                        <img src={project.image_url} alt={`${project.title}${categoryData?.name ? ` – ${categoryData.name}` : ""}`} loading="lazy" decoding="async" width={1200} height={675} className="absolute inset-0 w-full h-full object-cover" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-dark-foreground/20 text-6xl font-bold">{i + 1}</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-dark/60 to-transparent" />
                      {categoryData?.name && (
                        <div className="absolute top-3 right-3">
                          <Badge className="bg-primary text-primary-foreground text-xs">{categoryData.name}</Badge>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors duration-300" />
                    </div>

                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                        {project.title}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                        {meta.location && (
                          <span className="flex items-center gap-1"><MapPin size={14} />{meta.location}</span>
                        )}
                        {meta.year && (
                          <span className="flex items-center gap-1"><Calendar size={14} />{meta.year}</span>
                        )}
                      </div>
                      {project.subtitle && (
                        <p className="text-muted-foreground text-sm leading-relaxed line-clamp-2">{project.subtitle}</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {selectedProject && (() => {
        const meta = getProjectMeta(selectedProject.metadata);
        const categoryData = getCategoryData(selectedProject.categories ?? null);
        const highlights = meta.highlights;

        return (
          <div className="fixed inset-0 z-[60] bg-dark/80 flex items-center justify-center p-4" onClick={() => setSelectedProject(null)}>
            <div className="bg-card rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="aspect-[16/9] bg-dark relative rounded-t-2xl overflow-hidden">
                {selectedProject.image_url ? (
                  <img src={selectedProject.image_url} alt={selectedProject.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-dark-foreground/20 text-6xl font-bold">📷</span>
                  </div>
                )}
                <button type="button" onClick={() => setSelectedProject(null)} className="absolute top-3 left-3 w-8 h-8 rounded-full bg-dark/70 flex items-center justify-center text-dark-foreground hover:bg-dark transition-colors" aria-label="סגור">
                  <X size={18} />
                </button>
                {categoryData?.name && (
                  <Badge className="absolute top-3 right-3 bg-primary text-primary-foreground">{categoryData.name}</Badge>
                )}
              </div>

              <div className="p-6 md:p-8">
                <h2 className="text-2xl font-bold text-foreground mb-2">{selectedProject.title}</h2>
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  {meta.location && <span className="flex items-center gap-1"><MapPin size={14} />{meta.location}</span>}
                  {meta.year && <span className="flex items-center gap-1"><Calendar size={14} />{meta.year}</span>}
                </div>

                {selectedProject.content_html && (
                  <div className="text-muted-foreground leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedProject.content_html) }} />
                )}

                {highlights.length > 0 && (
                  <>
                    <h3 className="font-bold text-foreground mb-3">נקודות מפתח</h3>
                    <ul className="space-y-2 mb-6">
                      {highlights.map((highlight, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm">
                          <ChevronLeft className="text-primary flex-shrink-0" size={16} />
                          <span className="text-foreground">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                <div className="flex gap-3">
                  <Button asChild className="bg-primary text-primary-foreground hover:bg-gold-light font-bold flex-1">
                    <Link to="/contact" onClick={() => setSelectedProject(null)}>
                      רוצה פרויקט דומה? דברו איתנו
                    </Link>
                  </Button>
                  <Button type="button" onClick={() => setSelectedProject(null)} className="bg-primary text-primary-foreground hover:bg-gold-light font-bold">סגור</Button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <section className="py-16 bg-dark">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-dark-foreground mb-4">
            הפרויקט הבא יכול להיות <span className="text-primary">שלכם</span>
          </h2>
          <p className="text-dark-foreground/90 text-lg mb-8 max-w-xl mx-auto">
            רוצים לראות את הגג שלכם בגלריה הזו? צרו קשר ונתחיל לעבוד
          </p>
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-gold-light text-lg px-8 py-6 font-bold">
            <a href="/#contact">תיאום ביקור מקצועי</a>
          </Button>
        </div>
      </section>

      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Gallery;
